import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useEffect, useRef } from "react";
import { Message, Schema, useToaster } from "rsuite";
import styled from "styled-components";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import { SettingBody } from "../helpers/Admin_StyledComponents";
import {
  Button,
  FormActions,
  Input,
  SectionRibbon,
  Picker,
} from "../../../../ui";
// Requiring the lodash library
const _ = require("lodash");

export const ProfileSettings = (props) => {
  const { getAccessTokenSilently } = useAuth0();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [currentRoles, setCurrentRoles] = useState(props.roles);
  const [roles, setRoles] = useState([]);
  const [adminRoles, setAdminRoles] = useState([]);
  const [status, setStatus] = useState([]);
  const [baseRoles, setBaseRoles] = useState([]);
  const [blocked, setBlocked] = useState(false);
  const [disable, setDisable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [resMsg, setResMsg] = useState("");
  const toaster = useToaster();

  // FORM VALIDATION
  const [formError, setFormError] = useState({});

  // Validate phone number format
  const validatePhoneNumber = (phone) => {
    if (!phone || phone.trim() === "") {
      return true; // Empty is allowed
    }
    // Remove any non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, "");
    return digitsOnly.length === 10;
  };

  const prefillFormFields = () => {
    setFirstName(props.user.first_name);
    setLastName(props.user.last_name);
    setEmail(props.user.email_address);
    setPhoneNumber(props.user.phone_number);
    setStatus(props.user.status);
    setRoles(Object.values(props.user.roles)[0]);
    setBaseRoles(
      ["basic", "site-admin", "admin"].map((role) => ({
        label: role,
        value: role,
      })),
    );

    if (props.user.status === "deactivated") {
      setBlocked(true);
    } else {
      setBlocked(false);
    }
  };

  const onSubmit = async () => {
    setLoading(true);

    // Validate phone number
    if (!validatePhoneNumber(phoneNumber)) {
      setLoading(false);
      setFormError({ "phone-number": true });
      toaster.push(errPhoneMessage, "topCenter");
      console.error("Form Error: Invalid phone number");
      return;
    }

    // Clear any previous errors
    setFormError({});

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "POST",
        url: `/user/update/profile/`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          firstName: firstName,
          lastName: lastName,
          phoneNumber: phoneNumber,
          roleArray: roles,
          userEmail: email,
        }),
      });
      setResMsg(response.data);
      toaster.push(message, "topCenter");
    } catch (error) {
      console.error(error);
      toaster.push(errMessage, "topCenter");
    }
    setLoading(false);
  };

  const onSubmitUserStatus = async () => {
    setLoading(true);
    // console.log(`updateUserProfile`);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "POST",
        url: `/auth/user/toggle-block`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          user_id: email,
          blocked: !blocked,
        }),
      });
      setResMsg(response.data);
      toaster.push(message, "topCenter");
    } catch (error) {
      console.error(error);
      toaster.push(errMessage, "topCenter");
    }
    setLoading(false);
  };

  const onSubmitUserReset2FA = async () => {
    setLoading(true);
    // console.log(`updateUserProfile`);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "POST",
        url: `/auth/user/reset-2fa`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          user_id: email,
        }),
      });
      setResMsg(response.data);
      toaster.push(message, "topCenter");
    } catch (error) {
      console.error(error);
      toaster.push(errMessage, "topCenter");
    }
    setLoading(false);
  };

  const message = (
    <Message showIcon type="success" header="Success">
      You successfully updated <b>{firstName + " " + lastName}</b> profile.
    </Message>
  );

  const errMessage = (
    <Message showIcon type="error" header="Error">
      Something went wrong. Please try again.
    </Message>
  );

  const errPhoneMessage = (
    <Message showIcon type="error" header="Error">
      Phone Number must be empty or atleast 10 digits!
    </Message>
  );

  const checkButtonStatus = () => {
    if (
      firstName !== props.user.first_name ||
      lastName !== props.user.last_name ||
      email !== props.user.email_address ||
      _.isEqual(
        Object.values(props.user.roles["roles"]).sort(),
        roles?.sort(),
      ) === false ||
      phoneNumber !== props.user.phone_number
    ) {
      setDisable(false);
    } else {
      setDisable(true);
    }
  };

  useEffect(() => {
    prefillFormFields();
    setCurrentRoles(props.roles);
  }, [props.user]);

  useEffect(() => {
    checkButtonStatus();
  }, [firstName, lastName, phoneNumber, email, roles, currentRoles]);

  // Validate phone number on change
  useEffect(() => {
    if (phoneNumber && phoneNumber !== props.user.phone_number) {
      const isValid = validatePhoneNumber(phoneNumber);
      setFormError({ "phone-number": !isValid });
    } else {
      setFormError({});
    }
  }, [phoneNumber]);

  return (
    <SettingBody>
      <SectionRibbon title="Profile Settings" />

      <ModernFormContainer>
        <FormSection float="left">
          <Input
            label="First Name"
            value={firstName}
            onChange={setFirstName}
            placeholder="Enter first name"
          />
          <Input
            label="Last Name"
            value={lastName}
            onChange={setLastName}
            placeholder="Enter last name"
          />
          {currentRoles.includes("admin") ||
          currentRoles.includes("superuser") ||
          currentRoles.includes("site-admin") ? (
            <Picker
              label="Roles"
              value={roles}
              data={baseRoles}
              onChange={setRoles}
              placeholder="Select roles"
              multiple={true}
              cleanable={false}
            />
          ) : null}
        </FormSection>
        <FormSection float="right">
          <Input
            label="Email Address"
            value={email}
            disabled
            placeholder="Email address"
          />
          <Input
            label="Phone Number"
            value={phoneNumber}
            onChange={setPhoneNumber}
            placeholder="Enter phone number"
            error={
              formError["phone-number"]
                ? "Phone number must be 10 digits"
                : null
            }
          />
        </FormSection>
      </ModernFormContainer>

      <ModernButtonBar>
        {/* Destructive Actions First (Leftmost) */}
        <DestructiveActions>
          {currentRoles.includes("admin") ||
          currentRoles.includes("superuser") ? (
            status === "active" || status === "invited" ? (
              <Button
                variant="error"
                size="medium"
                onClick={onSubmitUserStatus}
                disabled={loading}
              >
                Deactivate User
              </Button>
            ) : (
              <Button
                variant="success"
                size="medium"
                onClick={onSubmitUserStatus}
                disabled={loading}
              >
                Reactivate User
              </Button>
            )
          ) : null}

          {currentRoles.includes("superuser") && status === "active" ? (
            <Button
              variant="subtle"
              size="medium"
              onClick={onSubmitUserReset2FA}
              disabled={loading}
            >
              Reset 2FA
            </Button>
          ) : null}
        </DestructiveActions>

        {/* Primary Actions (Rightmost) - Following Apple HIG */}
        <FormActions
          onCancel={prefillFormFields}
          onSubmit={onSubmit}
          cancelText="Reset"
          submitText="Save Changes"
          isSubmitting={loading}
          showCancel={!disable}
        />
      </ModernButtonBar>
    </SettingBody>
  );
};

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 45%;
  float: ${({ theme, float }) => float};
  gap: 20px;

  @media screen and (max-width: 768px) {
    width: 100%;
    float: none;
    margin-bottom: 20px;
  }
`;

const ModernButtonBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  margin-top: 32px;
  padding: 24px 32px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  gap: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    padding: 20px 24px;
    margin-top: 24px;
  }
`;

const DestructiveActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media screen and (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
    order: 2; /* Show after primary actions on mobile */
  }
`;

const ModernFormContainer = styled.div`
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.15) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  min-height: 300px;

  /* Clear floats properly */
  &::after {
    content: "";
    display: table;
    clear: both;
  }

  @media screen and (max-width: 768px) {
    padding: 24px;
    margin-bottom: 20px;
    min-height: auto;
  }
`;
